import { access, accessWith } from '@solid-primitives/utils';
import { createWritableMemo } from '@solid-primitives/memo';
import { createPolled } from '@solid-primitives/timer';
import { createSignal, createMemo, createComputed } from 'solid-js';
import { createStore } from 'solid-js/store';

// src/variables.ts
var SECOND = 1e3;
var MINUTE = 6e4;
var HOUR = 36e5;
var DAY = 864e5;
var WEEK = 6048e5;
var MONTH = 2592e6;
var YEAR = 31536e6;
var UNITS = [
  { max: 6e4, value: SECOND, name: "second" },
  { max: 276e4, value: MINUTE, name: "minute" },
  { max: 72e6, value: HOUR, name: "hour" },
  { max: 5184e5, value: DAY, name: "day" },
  { max: 24192e5, value: WEEK, name: "week" },
  { max: 28512e6, value: MONTH, name: "month" },
  { max: Infinity, value: YEAR, name: "year" }
];
var DEFAULT_MESSAGES = {
  justNow: "just now",
  past: (n) => n.match(/\d/) ? `${n} ago` : n,
  future: (n) => n.match(/\d/) ? `in ${n}` : n,
  month: (n, past) => n === 1 ? past ? "last month" : "next month" : `${n} month${n > 1 ? "s" : ""}`,
  year: (n, past) => n === 1 ? past ? "last year" : "next year" : `${n} year${n > 1 ? "s" : ""}`,
  day: (n, past) => n === 1 ? past ? "yesterday" : "tomorrow" : `${n} day${n > 1 ? "s" : ""}`,
  week: (n, past) => n === 1 ? past ? "last week" : "next week" : `${n} week${n > 1 ? "s" : ""}`,
  hour: (n) => `${n} hour${n > 1 ? "s" : ""}`,
  minute: (n) => `${n} minute${n > 1 ? "s" : ""}`,
  second: (n) => `${n} second${n > 1 ? "s" : ""}`
};

// src/utils.ts
var getDate = (init) => init instanceof Date ? init : new Date(init);
var getTime = (init) => typeof init === "number" ? init : init instanceof Date ? init.getTime() : new Date(init).getTime();
var getDateDifference = (from, to) => to.getTime() - from.getTime();
var getCountdown = (difference) => ({
  days: Math.floor(difference / DAY),
  hours: Math.floor(difference / HOUR % 24),
  minutes: Math.floor(difference / MINUTE % 60),
  seconds: Math.floor(difference / SECOND % 60),
  milliseconds: difference % 100
});
var formatDate = (date) => date.toISOString().slice(0, 10);
function formatDateRelative(difference, messages) {
  const _messages = {
    ...DEFAULT_MESSAGES,
    ...messages
  };
  const absDiff = Math.abs(difference);
  for (const unit of UNITS) {
    if (absDiff < unit.max) return format(unit);
  }
  return _messages.justNow;
  function applyFormat(name, val, isPast) {
    const formatter = _messages[name];
    if (typeof formatter === "function") return formatter(val, isPast);
    return formatter.replace("{0}", val.toString());
  }
  function format(unit) {
    const val = Math.round(absDiff / unit.value);
    const past = difference < 0;
    const str = applyFormat(unit.name, val, past);
    return applyFormat(past ? "past" : "future", str, past);
  }
}
var createDate = (init) => {
  const [date, setDate] = createWritableMemo(() => getDate(access(init)));
  const setter = (input) => setDate((prev) => getDate(accessWith(input, prev)));
  return [date, setter];
};
function createDateNow(interval = MINUTE / 2) {
  const [track, trigger] = createSignal(void 0, { equals: false });
  const memo = createPolled(
    () => {
      track();
      return /* @__PURE__ */ new Date();
    },
    interval,
    void 0,
    { equals: (a, b) => a.getTime() === b.getTime() }
  );
  return [memo, trigger];
}
function createTimeDifference(from, to) {
  const [fromDate] = createDate(from), [toDate] = createDate(to);
  const diff = createMemo(() => getDateDifference(fromDate(), toDate()));
  return [diff, { from: fromDate, to: toDate }];
}
function createTimeDifferenceFromNow(to, updateInterval = (diff) => Math.abs(diff) <= HOUR ? MINUTE / 2 : HOUR / 2) {
  const interval = typeof updateInterval === "function" ? () => updateInterval(diff()) : updateInterval;
  const [now, update] = createDateNow(interval);
  const [diff, { to: target }] = createTimeDifference(now, to);
  return [diff, { update, target, now }];
}
function createTimeAgo(to, options = {}) {
  const {
    min = MINUTE,
    max = Infinity,
    dateFormatter = formatDate,
    messages,
    relativeFormatter = (a, b, diff) => formatDateRelative(diff, messages)
  } = options;
  const [difference, extra] = createTimeDifferenceFromNow(to, options.interval);
  const timeAgo = createMemo(() => {
    const absDiff = Math.abs(difference());
    if (absDiff < min) return messages?.justNow ?? DEFAULT_MESSAGES.justNow;
    if (absDiff > max) return dateFormatter(extra.target());
    return relativeFormatter(extra.now(), extra.target(), difference());
  });
  return [timeAgo, { ...extra, difference }];
}
function createCountdown(a, b) {
  let difference;
  if (b !== void 0) difference = createTimeDifference(a, b)[0];
  else difference = a;
  const [countdown, setCountdown] = createStore(getCountdown(difference()));
  createComputed(() => setCountdown(getCountdown(difference())));
  return countdown;
}
function createCountdownFromNow(to, updateInterval = 1e3) {
  const [difference, extra] = createTimeDifferenceFromNow(to, updateInterval);
  const countdown = createCountdown(difference);
  return [countdown, extra];
}

export { DAY, DEFAULT_MESSAGES, HOUR, MINUTE, MONTH, SECOND, UNITS, WEEK, YEAR, createCountdown, createCountdownFromNow, createDate, createDateNow, createTimeAgo, createTimeDifference, createTimeDifferenceFromNow, formatDate, formatDateRelative, getCountdown, getDate, getDateDifference, getTime };
